/**
 * ConferenciaPage — Lista de Conferências (Verificação Lacres + Avulsa)
 * Rewritten for new conferência verificação system.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import { useConferenciaList } from '../hooks/useConferenciaList';
import { ConferenciaListTable } from '../components/conferencia/ConferenciaListTable';
import { ConferenciaAvulsaForm } from '../components/conferencia/ConferenciaAvulsaForm';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { Search, X, Filter, Trash2, FileText, Plus } from 'lucide-react';
import PermissionGate from '../components/PermissionGate';

export const ConferenciaPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    conferencias,
    allConferencias,
    loading,
    searchTerm,
    setSearchTerm,
    selectedHospitals,
    setSelectedHospitals,
    selectedClientes,
    setSelectedClientes,
    selectedStatus,
    setSelectedStatus,
    showFilters,
    setShowFilters,
    hospitalOptions,
    clienteOptions,
    statusOptions,
    activeFilterCount,
    handleClearFilters,
    handleCreateConferencia,
    handleCreateAvulsa,
    loadConferencias,
  } = useConferenciaList();

  const [showAvulsaForm, setShowAvulsaForm] = useState(false);
  const [allFiltersExpanded, setAllFiltersExpanded] = useState(false);

  // Navigate to detail page (create conferencia if needed)
  const handleConferir = async (row: any) => {
    if (row.id > 0) {
      // Conference already exists
      navigate(`/cloud/conferencia/${row.id}`);
    } else if (row.job_id) {
      // Need to create conference first
      const newId = await handleCreateConferencia(row.job_id, row.cliente_id || undefined);
      if (newId) {
        navigate(`/cloud/conferencia/${newId}`);
      }
    }
  };

  // Handle avulsa creation
  const handleAvulsaSubmit = async (clienteId: number, hospitalId: number) => {
    const newId = await handleCreateAvulsa(clienteId, hospitalId);
    if (newId) {
      setShowAvulsaForm(false);
      navigate(`/cloud/conferencia/${newId}`);
    }
  };

  if (loading) {
    return (
      <MainLayout className="dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Carregando conferências...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-2">
        {/* N167: o bloco do "Relatório: Conferências Avulsas" (Empresa + período
            + Gerar PDF/Excel) foi REMOVIDO a pedido do dono — o relatório que se
            usa é o UNITÁRIO, nos botões de cada linha da tabela abaixo.
            Os endpoints do geral seguem no ar (/relatorios/conferencias-avulsas e
            .../excel): não atrapalham e voltar a exibir é só recolocar o bloco. */}

        {/* Action Bar */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Conferências</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {conferencias.length} de {allConferencias.length} conferências
            </p>
          </div>
          <div className="flex gap-3">
            {/* Search */}
            <div className="relative hidden md:block w-72">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar Nº, lacre, OC, cliente, hospital..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filters Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                showFilters || activeFilterCount > 0
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
              {activeFilterCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Conferência Avulsa Button */}
            <PermissionGate tela="CONFERENCIA_VIEW" acao="criar">
              <button
                onClick={() => setShowAvulsaForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Conf. Avulsa
              </button>
            </PermissionGate>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
                <MultiSelectFilter
                  label="Hospital"
                  options={hospitalOptions}
                  selectedValues={selectedHospitals}
                  onChange={setSelectedHospitals}
                  placeholder="Todos"
                  expanded={allFiltersExpanded}
                  showTodos={true}
                  showBadges={true}
                />
                <MultiSelectFilter
                  label="Cliente"
                  options={clienteOptions}
                  selectedValues={selectedClientes}
                  onChange={setSelectedClientes}
                  placeholder="Todos"
                  expanded={allFiltersExpanded}
                  showTodos={true}
                  showBadges={true}
                />
                <MultiSelectFilter
                  label="Status"
                  options={statusOptions}
                  selectedValues={selectedStatus}
                  onChange={setSelectedStatus}
                  placeholder="Todos"
                  expanded={allFiltersExpanded}
                  showTodos={true}
                  showBadges={true}
                />
              </div>

              <div className="flex items-center gap-3 pb-0.5 shrink-0">
                <button
                  onClick={() => setAllFiltersExpanded(!allFiltersExpanded)}
                  className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-3 py-2 rounded-lg transition-colors"
                >
                  <span>{allFiltersExpanded ? '\u25BC' : '\u25B6'}</span>
                  <span className="whitespace-nowrap">{allFiltersExpanded ? 'Recolher' : 'Expandir'}</span>
                </button>
                <button
                  onClick={handleClearFilters}
                  className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-2 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="whitespace-nowrap">Limpar</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table or Empty State */}
        {conferencias.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center py-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 dark:text-gray-600 opacity-30 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {allConferencias.length === 0 ? 'Nenhuma conferência encontrada' : 'Nenhuma conferência com estes filtros'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {allConferencias.length === 0
                ? 'Contagens com lacres ou conferências avulsas aparecerão aqui'
                : 'Tente ajustar os filtros ou limpar a seleção'}
            </p>
          </div>
        ) : (
          <ConferenciaListTable
            conferencias={conferencias}
            onConferir={handleConferir}
          />
        )}

        {/* Avulsa Modal */}
        {showAvulsaForm && (
          <ConferenciaAvulsaForm
            onSubmit={handleAvulsaSubmit}
            onClose={() => setShowAvulsaForm(false)}
          />
        )}
      </div>
    </MainLayout>
  );
};
